import { useCallback, useRef, useState } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";

const SITE_KEY = import.meta.env.VITE_HCAPTCHA_SITE_KEY as string | undefined;

if (!SITE_KEY) {
  console.warn(
    "[auth] VITE_HCAPTCHA_SITE_KEY is not set. The CAPTCHA widget will not render, " +
      "and Supabase will reject sign-in/sign-up/password-reset while Attack Protection is on."
  );
}

/**
 * Shared CAPTCHA wiring for the auth forms. Supabase gates sign-in, sign-up and
 * password reset once Attack Protection is enabled, so all three need a token.
 */
export function useCaptcha() {
  const captchaRef = useRef<HCaptcha>(null);
  const [captchaToken, setCaptchaToken] = useState<string | undefined>();

  /**
   * hCaptcha tokens are single-use and Supabase consumes one per request, so a
   * spent token must be cleared after *every* attempt — including failures.
   * Without this a user who mistypes their password cannot retry: the second
   * submit reuses a dead token and fails with a confusing CAPTCHA error.
   */
  const resetCaptcha = useCallback(() => {
    captchaRef.current?.resetCaptcha();
    setCaptchaToken(undefined);
  }, []);

  const captcha = SITE_KEY ? (
    <div className="flex justify-center">
      <HCaptcha
        ref={captchaRef}
        sitekey={SITE_KEY}
        // The app is dark-only (index.html hardcodes <html class="dark">), so
        // match it rather than reading a theme that is never set.
        theme="dark"
        onVerify={(token) => setCaptchaToken(token)}
        onExpire={() => setCaptchaToken(undefined)}
        onError={() => setCaptchaToken(undefined)}
      />
    </div>
  ) : null;

  // With no site key configured the widget cannot render, so don't block the
  // form behind a token that can never arrive — let the server reject instead.
  const captchaReady = !SITE_KEY || Boolean(captchaToken);

  return { captcha, captchaToken, captchaReady, resetCaptcha };
}
